(function () {
  'use strict';

  // Phrases on a button/link's text that should trigger the game-picker modal,
  // regardless of where the link points. Matched case-insensitively against
  // textContent.trim().
  const TRIGGER_TEXT_PHRASES = [
    'pick your game',
    'pick a game',
    'choose your game',
    'choose a game',
    'select your game',
    'select a game',
  ];

  // Focusable selectors aligned with shadcn/Base UI Dialog behavior:
  // trap focus inside the overlay and restore it when the dialog closes.
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  let previouslyFocused = null;
  let leaving = false;

  // The sheet sits at the bottom of the modal, which would otherwise track the viewport. As a
  // game is tapped, phone browsers grow their toolbars for the page load, and a tracking sheet
  // jumps up by the toolbar's height. Its height is fixed in pixels instead, and stops following
  // the viewport once a game has been tapped.
  function pinToViewport(modal) {
    modal.style.height = window.innerHeight + 'px';
  }

  // While the next page loads, iPhone browsers also cut off anything fixed to the screen at a
  // shifted edge, uncovering the page under the sheet. Moved onto the page at the same spot, the
  // picker is drawn with the page and moves with it.
  function anchorToPage(modal) {
    modal.style.top = window.scrollY + 'px';
    modal.style.position = 'absolute';
  }

  function unanchor(modal) {
    modal.style.position = '';
    modal.style.top = '';
  }

  function isModalAvailable() {
    return !!document.getElementById('sm-modal');
  }

  function getDialog(modal) {
    return modal ? modal.querySelector('.sm-dialog') : null;
  }

  function getFocusable(container) {
    if (!container) return [];
    return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function openModal() {
    const modal = document.getElementById('sm-modal');
    if (!modal) return;
    previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    leaving = false;
    pinToViewport(modal);
    modal.classList.add('active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const dialog = getDialog(modal);
    const focusable = getFocusable(dialog);
    const initial = focusable[0] || dialog;
    if (initial && typeof initial.focus === 'function') {
      // Defer so the dialog is visible before focus moves (matches Dialog open timing).
      requestAnimationFrame(function () {
        initial.focus();
      });
    }
  }

  function closeModal() {
    const modal = document.getElementById('sm-modal');
    if (!modal) return;
    leaving = false;
    unanchor(modal);
    modal.classList.remove('active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
    previouslyFocused = null;
  }

  function isModalOpen() {
    const modal = document.getElementById('sm-modal');
    return !!modal && modal.classList.contains('active');
  }

  function trapFocus(e) {
    if (e.key !== 'Tab' || !isModalOpen()) return;

    const modal = document.getElementById('sm-modal');
    const dialog = getDialog(modal);
    const focusable = getFocusable(dialog);
    if (focusable.length === 0) {
      e.preventDefault();
      if (dialog) dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || !dialog.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
      return;
    }

    if (document.activeElement === last || !dialog.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    }
  }

  // Returns true if the click should be intercepted to open the game-picker modal.
  function shouldIntercept(target) {
    if (!target || target.nodeType !== 1) return false;

    // Highest priority: explicit opt-in via attribute on the element OR any ancestor.
    // This is the belt-and-suspenders mechanism: any element marked with
    // data-game-picker="true" (or just data-game-picker) will always open the modal.
    const optIn = target.closest('[data-game-picker="true"], [data-game-picker=""], [data-game-picker]:not([data-game-picker="false"])');
    if (optIn) return true;

    // Explicit opt-out via attribute on the element OR any ancestor.
    if (target.closest('[data-game-picker-ignore], [data-game-picker-disabled], [data-game-picker="false"]')) {
      return false;
    }

    // Skip clicks inside navigation menus (the header menu has its own behavior).
    if (target.closest('nav, header-menu, .menu-list, .header__menu')) return false;

    // Find the closest <a> or <button> the user actually meant to click.
    const el = target.closest('a, button');
    if (!el) return false;

    const rawText = (el.textContent || '').toLowerCase().trim();
    const href = el.getAttribute('href') || '';
    const lowerHref = href.toLowerCase();

    // Text-based heuristic: any "pick your game" / "choose your game" style label
    // should open the modal, regardless of where it tries to navigate.
    for (let i = 0; i < TRIGGER_TEXT_PHRASES.length; i += 1) {
      if (rawText.includes(TRIGGER_TEXT_PHRASES[i])) return true;
    }

    // External links: never intercept.
    if (lowerHref.startsWith('http') && !lowerHref.includes(window.location.hostname)) {
      return false;
    }

    // Anything explicitly pointing at the all-collections / catalog landing pages.
    const isCatalogLink =
      lowerHref === '/collections' ||
      lowerHref === '/collections/' ||
      lowerHref.includes('/collections/all') ||
      lowerHref.includes('shopify://collections/all') ||
      lowerHref === 'shopify://collections';
    if (isCatalogLink) return true;

    // "Shop now" / "shop all collection" style buttons that point at the catalog.
    const isShopText =
      rawText.includes('shop now') ||
      (rawText.includes('shop') && (rawText.includes('all') || rawText.includes('collection')));
    if (
      isShopText &&
      (lowerHref.includes('/collections/all') ||
        lowerHref.includes('shopify://collections/all') ||
        lowerHref === '/collections' ||
        lowerHref === '/collections/' ||
        lowerHref === 'shopify://collections')
    ) {
      return true;
    }

    return false;
  }

  // One global delegated click listener handles every potential trigger on the page.
  // Using delegation means we don't care if the trigger element is added later (theme
  // editor, AJAX, dynamic sections, etc.) — the click always bubbles to document.
  function attachDelegatedClickListener() {
    if (document.__gamePickerDelegated) return;
    document.__gamePickerDelegated = true;

    document.addEventListener(
      'click',
      function (e) {
        if (!isModalAvailable()) return;
        if (e.defaultPrevented) return;
        if (e.button !== 0) return; // only intercept primary button
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // let "open in new tab" through

        if (!shouldIntercept(e.target)) return;

        e.preventDefault();
        e.stopPropagation();
        openModal();
      },
      true // capture phase, so we run before any other handler can navigate.
    );
  }

  function attachModalChromeListeners() {
    const modal = document.getElementById('sm-modal');
    if (!modal || modal.__gamePickerWired) return;
    modal.__gamePickerWired = true;

    const closeBtn = modal.querySelector('#sm-close');
    const backdrop = modal.querySelector('.sm-backdrop');

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (backdrop) backdrop.addEventListener('click', closeModal);

    modal.addEventListener('click', function (e) {
      if (!e.target.closest('a.sm-card')) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      leaving = true;
      anchorToPage(modal);
    });
    window.addEventListener('resize', function () {
      if (isModalOpen() && !leaving) pinToViewport(modal);
    });
    // Coming back to this page from the back-forward cache, the picker is still open.
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      leaving = false;
      unanchor(modal);
      if (isModalOpen()) pinToViewport(modal);
    });

    document.addEventListener('keydown', function (e) {
      if (!isModalOpen()) return;
      if (e.key === 'Escape') {
        closeModal();
        return;
      }
      trapFocus(e);
    });
  }

  // Leaving through a game card: the cross-page transition would snapshot the open modal just as
  // phone Safari resizes its toolbar, and the snapshot lands shifted, flashing the page beneath.
  // The next page simply replaces this one instead.
  window.addEventListener('pageswap', function (e) {
    if (e.viewTransition && isModalOpen()) e.viewTransition.skipTransition();
  });

  function init() {
    attachDelegatedClickListener();
    attachModalChromeListeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-wire modal chrome if the modal section gets re-rendered in the theme editor.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      setTimeout(attachModalChromeListeners, 50);
    });
  }
})();
